import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import LandscapeOutlinedIcon from "@mui/icons-material/LandscapeOutlined";
import TheaterComedyOutlinedIcon from "@mui/icons-material/TheaterComedyOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout } from "../components/Dashboard";
import SeoHead from "../components/Seo/SeoHead";
import RUTA_TOKIO from "../data/rutaTokio";
import RutaPlaceDetail from "../components/Rutas/RutaPlaceDetail";
import "./RutasPage.css";

const ACTIVIDADES_TOKIO = [
	{
		href: "https://www.getyourguide.es/tokio-l193/tokio-teamlab-planets-tokyo-digital-art-museum-ticket-de-entrada-t260407/?partner_id=47M5PHY&utm_medium=online_publisher",
		Icon: AutoAwesomeOutlinedIcon,
		title: "TeamLab Borderless",
		desc: "Museo digital inmersivo",
	},
	{
		href: "https://www.getyourguide.es/tokio-l193/tokio-tour-gastronomico-por-shibuya-13-platos-y-4-restaurantes-t875628/?partner_id=47M5PHY&utm_medium=online_publisher",
		Icon: RestaurantOutlinedIcon,
		title: "Tour gastronómico",
		desc: "Ramen, takoyaki y más",
	},
	{
		href: "https://www.getyourguide.es/tokio-l193/tokio-tour-a-pie-de-lo-mas-destacado-de-shibuya-y-callejuelas-secretas-t870938/?partner_id=47M5PHY&utm_medium=online_publisher",
		Icon: DirectionsWalkOutlinedIcon,
		title: "Tour Shibuya",
		desc: "Paseo guiado por los barrios icónicos",
	},
	{
		href: "https://www.getyourguide.es/tokio-l193/tokio-tour-guiado-en-kart-por-las-calles-de-shibuya-y-shinjuku-t928764/?partner_id=47M5PHY&utm_medium=online_publisher",
		Icon: DirectionsCarOutlinedIcon,
		title: "Go-kart por Tokio",
		desc: "Conduce disfrazado por las calles",
	},
	{
		href: "https://www.getyourguide.es/tokio-l193/tokio-monte-fuji-pagoda-chureito-lago-kawaguchi-y-oshino-hakkai-t792363/?partner_id=47M5PHY&utm_medium=online_publisher",
		Icon: LandscapeOutlinedIcon,
		title: "Excursión al Monte Fuji",
		desc: "Excursión de un día desde Tokio con guía",
	},
	{
		href: "https://www.getyourguide.es/tokio-l193/tokio-espectaculo-y-experiencia-de-sumo-en-shinjuku-con-foto-t1042988/?partner_id=47M5PHY&utm_medium=online_publisher",
		Icon: TheaterComedyOutlinedIcon,
		title: "Espectáculo de sumo",
		desc: "Espectáculo en Shinjuku",
	},
];

const RutasPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, handleLogout: contextLogout } = useFirebase();

	const handleBack = () => {
		const from = location.state?.from;
		if (
			typeof from === "string" &&
			from.startsWith("/") &&
			from !== "/rutas/tokio"
		) {
			navigate(from);
			return;
		}
		if (typeof window !== "undefined" && window.history.length > 1) {
			navigate(-1);
			return;
		}
		navigate("/rutas");
	};

	const [activeDay, setActiveDay] = useState(0);
	const [selectedPlace, setSelectedPlace] = useState(null);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 920);
	const completedDays = [];
	const dayPanelRef = useRef(null);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 920);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);
	const ruta = RUTA_TOKIO;
	const currentDay = ruta.days[activeDay];

	const selectedIllustration =
		selectedPlace != null
			? ruta.days[selectedPlace.dayIndex]?.illustrations?.[
					selectedPlace.illIndex
				]
			: null;

	const showDayPanel = selectedPlace == null;

	const scrollToPanel = () => {
		if (window.innerWidth <= 920 && dayPanelRef.current) {
			setTimeout(() => {
				dayPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 100);
		}
	};

	const selectDay = (idx) => {
		setActiveDay(idx);
		setSelectedPlace(null);
		scrollToPanel();
	};

	const selectPlace = (dayIndex, illIndex, e) => {
		e.stopPropagation();
		setActiveDay(dayIndex);
		setSelectedPlace({ dayIndex, illIndex });
		scrollToPanel();
	};

	const selectDayFromDot = (dayIndex, e) => {
		e.stopPropagation();
		setActiveDay(dayIndex);
		setSelectedPlace(null);
		scrollToPanel();
	};

	const formattedUser = user
		? { displayName: user.displayName, email: user.email }
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home") navigate("/");
		else if (sectionId === "explore") navigate("/explorar");
		else if (sectionId === "passport") navigate("/passport");
		else if (sectionId === "rutas") navigate("/rutas");
		else if (sectionId === "my-guides") navigate("/mis-guias");
		else if (sectionId === "favorites") navigate("/favoritos");
		else if (sectionId === "modules") navigate("/modulos");
		else if (sectionId === "discounts") navigate("/descuentos");
		else if (sectionId === "travel-card") navigate("/tarjeta-viajera");
		else if (sectionId === "create") navigate("/crear-guia");
		else navigate("/");
	};

	const handleLogout = async () => {
		try {
			await contextLogout();
			navigate("/");
		} catch (e) {
			console.error(e);
		}
	};

	return (
		<>
			<SeoHead
				title={`Ruta ${ruta.title} en ${ruta.stats.days} días — Viajes Molones`}
				description={ruta.description}
				path="/rutas/tokio"
			/>
			<DashboardLayout
				user={formattedUser}
				activeSection="rutas"
				onSectionChange={handleSectionChange}
				onLogin={() => navigate("/")}
				onSignUp={() => navigate("/")}
				onLogout={handleLogout}
				onCreateNew={() => navigate("/crear-guia")}
				onSearch={() => {}}
				prebuiltGuides={[]}
				myGuides={[]}
				imageMap={{}}
			>
				<Box sx={{ mt: { xs: 1, md: 0 } }}>
					<div className="rutas-page">
						<button
							type="button"
							className="rutas-page-back"
							onClick={handleBack}
						>
							<ArrowBackIcon sx={{ fontSize: 18 }} />
							Volver
						</button>
						<div className="rutas-page-heading">
							<h1 className="rutas-page-title">Ruta 4 días por Tokio</h1>
							<Link to="/tokio" className="rutas-guide-cta">
								<MenuBookOutlinedIcon sx={{ fontSize: 18 }} />
								Ver la guía de Tokio
							</Link>
						</div>

						{/* Hero: Mapa + Día seleccionado */}
						<div className="rutas-hero">
							{/* Panel izquierdo: Mapa visual */}
							<div className="rutas-map-panel">
								{/* Mapa ilustrado de fondo */}
								<img
									src={`${process.env.PUBLIC_URL || ""}/mapa-tokio-4.png`}
									alt="Mapa ilustrado de Tokio"
									className="rutas-map-bg"
								/>

								{/* Puntos interactivos sobre el mapa */}
								{ruta.days.map((day, idx) => (
									<div
										key={day.id}
										className="rutas-map-spot"
										style={{
											top: day.mapPosition.top,
											left: day.mapPosition.left,
										}}
									>
										{day.illustrations &&
											day.illustrations.map((ill, i) => (
												<div
													key={i}
													className={`rutas-spot-ill-wrapper ${
														ill.anchor === "center"
															? "rutas-spot-ill-wrapper--center"
															: ""
													} ${
														selectedPlace?.dayIndex === idx &&
														selectedPlace?.illIndex === i
															? "selected"
															: ""
													}`}
												style={{
													top: (isMobile && ill.mobileOffset?.top) || ill.offset.top,
													left: (isMobile && ill.mobileOffset?.left) || ill.offset.left,
												}}
													onClick={(e) => selectPlace(idx, i, e)}
												>
													<img
														src={`${process.env.PUBLIC_URL || ""}${ill.src}`}
														alt={ill.name}
														className="rutas-spot-img"
													/>
													<span className="rutas-spot-ill-name">
														{ill.name}
													</span>
												</div>
											))}
										<div
											className={`rutas-stop-dot ${
												completedDays.includes(day.id) ? "completed" : ""
											} ${activeDay === idx && showDayPanel ? "active" : ""}`}
											style={
												day.dotOffset
													? {
															top: day.dotOffset.top,
															left: day.dotOffset.left,
															position: "absolute",
														}
													: undefined
											}
											onClick={(e) => selectDayFromDot(idx, e)}
										>
											{completedDays.includes(day.id) ? "✓" : `Día ${day.id}`}
										</div>
									</div>
								))}
							</div>

							{/* Panel derecho: Detalle del día */}
							<div className="rutas-day-panel-wrapper" ref={dayPanelRef}>
								<div className="rutas-day-panel">
									{/* Tabs */}
									<div className="rutas-day-tabs">
										{ruta.days.map((day, idx) => (
											<button
												key={day.id}
												className={`rutas-day-tab ${
													activeDay === idx ? "active" : ""
												}`}
												onClick={() => selectDay(idx)}
											>
												DÍA {day.id}
											</button>
										))}
									</div>

									<div className="rutas-day-content">
										{showDayPanel ? (
											<>
												<div className="rutas-day-header">
													<div
														style={{
															display: "flex",
															alignItems: "center",
														}}
													>
														<div className="rutas-day-number">
															{currentDay.id}
														</div>
														<div>
															<h2 className="rutas-day-title">
																{currentDay.title}
															</h2>
															<span className="rutas-day-zone">
																{currentDay.zone}
															</span>
														</div>
													</div>
												</div>

												<p className="rutas-day-description">
													{currentDay.description}
												</p>

												<div className="rutas-day-places-label">
													Lugares del día
												</div>
												<div className="rutas-day-places-list">
													{currentDay.illustrations?.map((ill, i) => (
														<button
															key={i}
															className="rutas-day-place-btn"
															onClick={() =>
																setSelectedPlace({
																	dayIndex: activeDay,
																	illIndex: i,
																})
															}
														>
															<img
																src={`${process.env.PUBLIC_URL || ""}${ill.src}`}
																alt={ill.name}
																className="rutas-day-place-btn-img"
															/>
															<span>{ill.name}</span>
														</button>
													))}
												</div>

												<div className="rutas-highlights-title">
													IMPERDIBLES
												</div>
												<div className="rutas-highlights">
													{currentDay.highlights.map((h) => (
														<span key={h} className="rutas-highlight-chip">
															{h}
														</span>
													))}
												</div>
											</>
										) : (
											selectedIllustration?.info && (
												<>
													<button
														type="button"
														className="rutas-place-back"
														onClick={() => setSelectedPlace(null)}
													>
														← Ver día {currentDay.id}
													</button>

													<div className="rutas-place-photo">
														<img
															src={`${process.env.PUBLIC_URL || ""}${selectedIllustration.photo || selectedIllustration.src}`}
															alt={selectedIllustration.name}
														/>
													</div>
													<RutaPlaceDetail info={selectedIllustration.info} />
												</>
											)
										)}
									</div>
								</div>
							</div>
						</div>
						<Link to="/tokio" className="rutas-guide-banner">
							<div className="rutas-guide-banner-text">
								<strong>Ver la guía de Tokio</strong>
								<span>
									Esta ruta es el mapa de 4 días. La guía tiene el
									itinerario de 6 días y la preview.
								</span>
							</div>
							<span className="rutas-guide-banner-arrow">→</span>
						</Link>

						<div className="rutas-affiliate-section">
							{/* IATI Seguros */}
							<a
								href="https://www.iatiseguros.com?r=97300500080093"
								target="_blank"
								rel="noopener noreferrer"
								className="rutas-affiliate-card rutas-affiliate-card--iati"
							>
								<div className="rutas-affiliate-badge">Recomendado</div>
								<div className="rutas-affiliate-icon">🛡️</div>
								<div className="rutas-affiliate-content">
									<h4>Seguro de viaje IATI</h4>
									<p>
										5% de descuento en tu seguro para Japón. Cobertura médica,
										cancelación y equipaje.
									</p>
								</div>
								<span className="rutas-affiliate-cta">Obtener descuento →</span>
							</a>

							{/* Actividades GetYourGuide */}
							<div className="rutas-activities-card">
								<div className="rutas-activities-header">
									<span className="rutas-activities-title">
										Actividades en Tokio
									</span>
								</div>
								<div className="rutas-activities-scroll">
									{ACTIVIDADES_TOKIO.map((act) => (
										<a
											key={act.title}
											href={act.href}
											target="_blank"
											rel="noopener noreferrer"
											className="rutas-activity-item"
										>
											<div className="rutas-activity-item-top">
												<div className="rutas-activity-icon">
													<act.Icon sx={{ fontSize: 20 }} />
												</div>
												<div className="rutas-activity-info">
													<strong>{act.title}</strong>
													<span>{act.desc}</span>
												</div>
											</div>
											<ArrowForwardIcon
												className="rutas-activity-arrow-icon"
												sx={{ fontSize: 16 }}
											/>
										</a>
									))}
								</div>
							</div>
						</div>
					</div>
				</Box>
			</DashboardLayout>
		</>
	);
};

export default RutasPage;
